/**
 * UI Asset Preloader Service
 * Ensures ALL UI assets (fonts, icons, logos) are loaded BEFORE React renders
 * This prevents users from seeing icon fallback text or broken UI
 */

import logger from '../utils/logger.js';

class UIAssetPreloader {
  constructor() {
    this.loaded = false;
    this.loadingPromise = null;
  }

  /**
   * Preload all critical UI assets
   * @returns {Promise<void>}
   */
  async preloadAllUIAssets() {
    // Return cached promise if already loading
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    // Return immediately if already loaded
    if (this.loaded) {
      return Promise.resolve();
    }

    logger.log('🎨 Starting UI asset preload...');

    this.loadingPromise = this._loadAssets();
    await this.loadingPromise;
    this.loaded = true;

    logger.log('✅ UI assets preloaded successfully');
    return Promise.resolve();
  }

  async _loadAssets() {
    const startTime = performance.now();

    const assets = [
      // Material Icons Font (CRITICAL - 3.6MB)
      // This is the font used by MaterialIcon component
      { type: 'font', family: 'Material Symbols Outlined', desc: 'Material Icons font' },
      
      // Logo assets for ClothedEyeLoader
      { type: 'image', url: '/assets/svgs/clothedEye-loader-spin.svg', desc: 'Loader spin gear' },
      { type: 'image', url: '/assets/svgs/clothedEye-loader-static.svg', desc: 'Loader static gear' },
      
      // Fallback logo
      { type: 'image', url: '/assets/images/clothed_eye_eye_lg.png', desc: 'Main logo' }
    ];

    try {
      // Load all assets in parallel
      const results = await Promise.allSettled(
        assets.map(asset => this._loadSingleAsset(asset))
      );

      // Log any failures (but don't block the app)
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          logger.warn(`⚠️ Failed to load ${assets[index].desc}:`, result.reason);
        }
      });

      const endTime = performance.now();
      const duration = Math.round(endTime - startTime);
      logger.log(`⏱️ UI assets loaded in ${duration}ms`);

    } catch (error) {
      logger.error('❌ UI asset preload error:', error);
      throw error;
    }
  }

  /**
   * Load a single asset
   * @param {Object} asset - Asset configuration
   * @returns {Promise<void>}
   */
  async _loadSingleAsset(asset) {
    if (asset.type === 'font') {
      return this._loadFont(asset);
    }
    if (asset.type === 'image') {
      return this._loadImage(asset);
    }
    throw new Error(`Unknown asset type: ${asset.type}`);
  }

  /**
   * Load a font using the Font Loading API
   * @param {Object} asset - Font asset configuration
   * @returns {Promise<FontFace>}
   */
  async _loadFont(asset) {
    try {
      // Use the Font Loading API to ensure font is ready
      // This will use the @font-face definition from MaterialIcon.css
      await document.fonts.load(`1em "${asset.family}"`);
      
      // Verify the font is actually loaded
      const fontAvailable = document.fonts.check(`1em "${asset.family}"`);
      if (!fontAvailable) {
        throw new Error(`Font "${asset.family}" not available after load`);
      }
      
      logger.log(`✓ Loaded font: ${asset.family}`);
      return Promise.resolve();
    } catch (error) {
      logger.error(`✗ Failed to load font: ${asset.family}`, error);
      throw error;
    }
  }

  /**
   * Preload an image
   * @param {Object} asset - Image asset configuration
   * @returns {Promise<void>}
   */
  _loadImage(asset) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      const timeout = setTimeout(() => {
        reject(new Error(`Image load timeout: ${asset.url}`));
      }, 5000); // 5 second timeout

      img.onload = () => {
        clearTimeout(timeout);
        logger.log(`✓ Loaded image: ${asset.desc}`);
        resolve();
      };

      img.onerror = (error) => {
        clearTimeout(timeout);
        logger.warn(`✗ Failed to load image: ${asset.desc}`, error);
        // Resolve anyway - don't block the app for missing images
        resolve();
      };

      img.src = asset.url;
    });
  }

  /**
   * Check if UI assets are loaded
   * @returns {boolean}
   */
  isLoaded() {
    return this.loaded;
  }

  /**
   * Reset the preloader state (for testing)
   */
  reset() {
    this.loaded = false;
    this.loadingPromise = null;
  }
}

// Export singleton instance
const uiAssetPreloader = new UIAssetPreloader();
export default uiAssetPreloader;

